/**
 * Paints the world map (equirectangular) onto a 2D canvas. The same picture
 * is the 3D globe's texture and the Lite-mode 2D map, so both modes always
 * agree. No three.js.
 *
 * Colour = your status in that market (never the only signal: the side
 * panel and the markets table say it in words):
 *   HQ          bright cyan, outlined
 *   operating   cyan
 *   establishing gold
 *   available   slate (a market you could enter)
 *   not a market dark (no gameplay there)
 */
import { MARKET_ANCHOR, atlasName, latLonToPixel, unwrapRing } from "./geo.js";

const STATUS_COLOR = {
  hq: "#7ee8fa",
  operating: "#3fb8d4",
  establishing: "#fbbf24",
  available: "#3b4770",
  none: "#1b2238",
};
const OCEAN = "#070b18";
const BORDER = "rgba(160, 190, 255, 0.28)";

function statusByAtlasName(world) {
  const m = new Map();
  for (const c of (world && world.countries) || []) m.set(atlasName(c.country), c.status);
  return m;
}

/**
 * Draw a polygon's rings as one path. A ring that crosses the antimeridian
 * (Fiji, Chukotka) is unwrapped to continuous longitudes and drawn again one
 * map-width over, so it never streaks across the map.
 */
function tracePolygon(ctx, polygon, W, H) {
  for (const raw of polygon) {
    const ring = unwrapRing(raw);
    const lons = ring.map((p) => p[0]);
    const shifts = [0];
    if (Math.min(...lons) < -180) shifts.push(W);
    if (Math.max(...lons) > 180) shifts.push(-W);
    for (const shift of shifts) {
      ring.forEach(([lon, lat], i) => {
        const [x, y] = latLonToPixel(lat, lon, W, H);
        if (i === 0) ctx.moveTo(x + shift, y);
        else ctx.lineTo(x + shift, y);
      });
      ctx.closePath();
    }
  }
}

/**
 * paintMap(ctx, W, H, countries, world, { highlight })
 * countries: decodeTopology() output. world: GET /api/world (may be null).
 * highlight: a market name to outline (the selection).
 */
function paintMap(ctx, W, H, countries, world, { highlight = null, glowOnly = false } = {}) {
  const status = statusByAtlasName(world);
  ctx.save();
  ctx.fillStyle = glowOnly ? "#000" : OCEAN;
  ctx.fillRect(0, 0, W, H);
  if (!glowOnly) {
    // graticule
    ctx.strokeStyle = "rgba(120, 160, 255, 0.07)";
    ctx.lineWidth = Math.max(1, W / 2048);
    for (let lon = -180; lon <= 180; lon += 30) {
      const [x] = latLonToPixel(0, lon, W, H);
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
    }
    for (let lat = -60; lat <= 60; lat += 30) {
      const [, y] = latLonToPixel(lat, 0, W, H);
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
    }
  }
  for (const c of countries) {
    const st = status.get(c.name) || "none";
    if (glowOnly && !(st === "hq" || st === "operating" || st === "establishing")) continue;
    ctx.beginPath();
    for (const poly of c.polygons) {
      tracePolygon(ctx, poly, W, H);
    }
    ctx.fillStyle = STATUS_COLOR[st];
    ctx.fill("evenodd");
    if (!glowOnly) {
      ctx.strokeStyle = BORDER;
      ctx.lineWidth = Math.max(1, W / 2048);
      ctx.stroke();
    }
  }
  // markets too small for a polygon at this scale (Singapore, Hong Kong): city dots
  for (const c of (world && world.countries) || []) {
    if (countries.some((k) => k.name === atlasName(c.country))) continue;
    const [lat, lon] = MARKET_ANCHOR[c.country] || [0, 0];
    const [x, y] = latLonToPixel(lat, lon, W, H);
    if (glowOnly && c.status === "available") continue;
    ctx.beginPath();
    ctx.arc(x, y, Math.max(3, W / 300), 0, Math.PI * 2);
    ctx.fillStyle = STATUS_COLOR[c.status] || STATUS_COLOR.available;
    ctx.fill();
  }
  if (highlight) {
    const name = atlasName(highlight);
    const k = countries.find((c) => c.name === name);
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = Math.max(2, W / 700);
    if (k) {
      ctx.beginPath();
      for (const poly of k.polygons) tracePolygon(ctx, poly, W, H);
      ctx.stroke();
    } else if (MARKET_ANCHOR[highlight]) {
      const [x, y] = latLonToPixel(...MARKET_ANCHOR[highlight], W, H);
      ctx.beginPath(); ctx.arc(x, y, Math.max(6, W / 160), 0, Math.PI * 2); ctx.stroke();
    }
  }
  // HQ outline, always
  const hq = ((world && world.countries) || []).find((c) => c.status === "hq");
  if (hq && !glowOnly) {
    const k = countries.find((c) => c.name === atlasName(hq.country));
    if (k) {
      ctx.strokeStyle = "#e6fbff";
      ctx.lineWidth = Math.max(1.5, W / 1200);
      ctx.beginPath();
      for (const poly of k.polygons) tracePolygon(ctx, poly, W, H);
      ctx.stroke();
    }
  }
  ctx.restore();
}

export { OCEAN, STATUS_COLOR, paintMap };
